import React, { Component } from 'react';

import './App.css';
import{ withStyles, WithStyles } from "@mui/styles";
import MainMenu from "./components/app-bar/MainMenu";
import Routes from './components/app-bar/Routes';
import {
    fetchAuthenticatedUser,
    localStorageTenantIdKey,
    userTokenRefresh
} from "./actions/common/AuthenticatedUserActions";
import {connect} from "react-redux";
import {withRouter, RouteComponentProps } from "react-router-dom";
import EventNotificationBar from "./components/common/EventNotificationBar";
import { Theme } from '@mui/material';
import { AppState } from './reducers/RootReducer';
import { EventNotificationState } from './reducers/common/EventNotificationReducer';
import { AuthenticatedUserState } from './reducers/common/AuthenticatedUserReducer';
import { compose } from 'redux';
import {selectLanguage} from "./actions/common/GeneralSettingsActions";
import {LanguageState} from "./reducers/common/GeneralSettingsReducer";
import {ViewSettingsState} from "./reducers/common/ViewSettingsReducer";
import {MAIN_APP_TITLE} from "./config/GeneralConfig";
import {
    activateUnsavedDataProtection,
    disableUnsavedDataProtection, sendUserToPage,
    sendUserToPageSuccess
} from "./actions/common/UserViewSettingsActions";
import {AuthenticatedUserContext, LanguageContext} from './config/ContextConfig';
import ErrorBoundary from "./components/common/ErrorBoundary";
import {Footer} from "./components/common/Footer";
import {buttonStyles} from "./config/Theme";
import {initialGuestUser} from "./models/users/AuthenticatedUser";
import NavDrawer from "./components/app-bar/NavDrawer";
import {MainMenuState} from "./reducers/menu/MainMenuReducer";
import {getLanguageByBrowserLanguage, getLanguageByLocale, languageLocalStorageKey} from "./config/LanguageConfig";
import {getLocalStorage} from "./helpers/Storage";
import {makeStringId} from "./models/common/Identifier";
import {getAuthToken, isAuthTokenAboutToExpire} from "./config/RouteConfig";

/**
 * typescript declarations
 */
interface AppComponentProps extends WithStyles< typeof styles >, RouteComponentProps<any> {
    eventBar: EventNotificationState
    authUser: AuthenticatedUserState
    fetchAuthenticatedUser: typeof fetchAuthenticatedUser
    userTokenRefresh: typeof userTokenRefresh
    selectLanguage: typeof selectLanguage
    language: LanguageState
    viewSettings: ViewSettingsState
    mainMenu: MainMenuState
    sendUserToPageSuccess: typeof sendUserToPageSuccess
    sendUserToPage: typeof sendUserToPage
    activateUnsavedDataProtection: typeof activateUnsavedDataProtection
    disableUnsavedDataProtection: typeof disableUnsavedDataProtection

}

const styles = ( theme: Theme ) => ({
    ...buttonStyles( theme ),
    root: { width: '100%', },
    content: {
        paddingTop: '60px',
        [ theme.breakpoints.down('xs') ]: {
            paddingTop: '55px'
        }
    },
    loginForm: {
        width: 90+"%",
        margin: "auto",
        marginTop: "55px",
        [ theme.breakpoints.up('sm') ]: {
            width: "40%",
        }
    },
    header: {
        zIndex: 1
    },
    desktopPadding: {
        paddingLeft: '0',
        [theme.breakpoints.up('md')]: {
            paddingLeft: '70px',
        }
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: "flex"
        },
    },
    sectionMobile: {
        display: "flex",
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
});

interface AppComponentState {
    navDrawOpen: boolean
}

class App extends Component< AppComponentProps, AppComponentState> {

    constructor(props: AppComponentProps) {
        super( props );

        this.state = { navDrawOpen: false };
    }

    private static readonly TOKEN_REFRESH_INTERVAL_MS = 120 * 1000;
    private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

    componentDidMount() {
        document.title = this.getViewTitle();

        this.authTokenRefresh();

        // Schedule proactive token refresh every 4 minutes 45 seconds
        this.tokenRefreshTimer = setInterval(() => {
            this.authTokenRefresh();
        }, App.TOKEN_REFRESH_INTERVAL_MS);

    }

    componentWillUnmount() {
        // Clean up the interval to prevent memory leaks
        if (this.tokenRefreshTimer !== null) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    }

    componentDidUpdate() {
        document.title = this.getViewTitle();

        const { redirect } = this.props.viewSettings;

        if( this.props.location.pathname.includes("edit") || this.props.location.pathname.includes("new") ) {

            if( ! this.props.viewSettings.unsavedDataProtection ) {
                this.props.activateUnsavedDataProtection();
            }
        } else {
            if( this.props.viewSettings.unsavedDataProtection ) {
                this.props.disableUnsavedDataProtection();
            }
        }

        //get language selection from local storage
        const localStorageLanguage = window.localStorage.getItem(languageLocalStorageKey);

        if( localStorageLanguage !== null && localStorageLanguage !== this.props.language.current.locale ) {

            this.props.selectLanguage(
                getLanguageByLocale( localStorageLanguage )
            );

        } else if( localStorageLanguage === null ) {
            const browserLanguage = getLanguageByBrowserLanguage();

            window.localStorage.setItem( languageLocalStorageKey, browserLanguage.locale );
            this.props.selectLanguage( browserLanguage );
        }

        this.authTokenRefresh()


        if( redirect !== null ) {
            //@ts-ignore
            this.props.history.push( redirect.path );
            this.props.sendUserToPageSuccess( redirect.viewIdentifier );
        }
    }

    authTokenRefresh() {


        // get tenant from local storage and refresh token, when window.token is not set
        const tenantId = getLocalStorage(localStorageTenantIdKey, "");
        const authToken = getAuthToken() ?? "";

        if( tenantId !== null
            && tenantId.length > 0
            && ( authToken === "" || isAuthTokenAboutToExpire(authToken) )
            && this.props.authUser.isRefreshPossible
        )
        {
            this.props.userTokenRefresh( makeStringId( tenantId ) );
        }
    }


    getViewTitle( isDesktop: boolean = true ): string {
        if( this.props.viewSettings.currentViewTitle !== '' && isDesktop ) {
           return `${ MAIN_APP_TITLE } - ${ this.props.viewSettings.currentViewTitle }`;
        } else {
            return MAIN_APP_TITLE;
        }
    }

    getEventNotificationBar() {
        const { eventBar } = this.props;
        if( eventBar.isVisible ) {
            return (
                <EventNotificationBar
                    isVisible={ eventBar.isVisible }
                    messageType={eventBar.messageType}
                    message={ eventBar.message }
                />
            )
        } else {
            return null;
        }
    }

  render() {

    const { classes, language } = this.props;


        return (
            <ErrorBoundary language={language.current}>
                <LanguageContext.Provider value={ language.current }>
                    <AuthenticatedUserContext.Provider value={ this.props.authUser.current ?? initialGuestUser()  } >
                        <div className="App app-body">
                            <div className="flex">
                                {this.getEventNotificationBar()}
                                <div>
                                    <NavDrawer
                                        language={ this.props.language.current }
                                        isOpen={  this.state.navDrawOpen  }
                                        viewSettingsState={ this.props.viewSettings }
                                        sendUserToPage={ this.props.sendUserToPage }
                                        isAppBarOpen={ ( this.props.authUser?.current?.id?.rawValue ?? 0 ) > 0 }
                                        toggleDrawer={ _isOpen => this.setState({ navDrawOpen: _isOpen }) }
                                        navLinks={ this.props.mainMenu.menu.navItems }
                                        viewTitle={this.getViewTitle()}
                                    />
                                </div>
                                <div style={ { flexGrow: 1 }} className={ classes.desktopPadding }>
                                    <div className={classes.sectionDesktop}>
                                        <MainMenu
                                            //@ts-ignore
                                            onMenuBtnClick={ () => this.setState({ navDrawOpen: !this.state.navDrawOpen }) }
                                            viewTitle={this.getViewTitle()}
                                            language={language.current}
                                        />
                                    </div>
                                    <div className={classes.sectionMobile}>
                                        <MainMenu
                                            //@ts-ignore
                                            onMenuBtnClick={ () => this.setState({ navDrawOpen: !this.state.navDrawOpen }) }
                                            viewTitle={this.getViewTitle(false)}
                                            language={language.current}
                                        />
                                    </div>

                                    <div className={classes.content}>
                                        <Routes/>
                                    </div>
                                </div>

                            </div>

                        </div>
                        <Footer/>
                    </AuthenticatedUserContext.Provider>
                </LanguageContext.Provider>
            </ErrorBoundary>
        );
  }
}

//redux state to props function
function mapStateToProps(state: AppState, ownProps: AppComponentProps) {
    return {
        authUser: state.AuthenticatedUserReducer,
        eventBar: state.EventNotificationReducer,
        language: state.LanguageReducer,
        viewSettings: state.ViewSettingsReducer,
        mainMenu: state.MainMenuReducer,
    }
}

//map redux actions to prop action, to dispatch the actions from the component
const mapDispatchToProps = {
    fetchAuthenticatedUser,
    selectLanguage,

    sendUserToPage,
    userTokenRefresh,
    sendUserToPageSuccess,
    activateUnsavedDataProtection,
    disableUnsavedDataProtection
};

export default compose<React.ComponentClass>(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    withStyles( styles )
)( App );
